<template>
  <div
    v-if="visible"
    class="complete-payment-modal reference-payment-layout"
    role="dialog"
    aria-modal="true"
  >
    <div class="complete-payment-backdrop" @click="cancel"></div>

    <section class="complete-payment-card">
      <header class="payment-modal-header">
        <div class="payment-modal-heading">
          <span class="payment-modal-icon"><i class="fas fa-money-bill-wave"></i></span>
          <strong>{{ $t('retailSale.paymentModal.title') }}</strong>
          <span class="order-badge">{{
            $t('retailSale.paymentModal.order', { order: order.id })
          }}</span>
          <span class="heading-separator">·</span>
          <span class="customer-name">{{
            customerName || $t('retailSale.customer.walkInCustomer')
          }}</span>
        </div>
        <div class="payment-modal-actions">
          <span v-if="exchangeRateLabel" class="exchange-chip">
            <i class="fas fa-arrows-rotate"></i>
            <strong>1 {{ currencyCode }}</strong>
            <span class="exchange-equals">=</span>
            <strong class="exchange-target">{{ exchangeRateAmount }}</strong>
            <span class="exchange-code">{{ exchangeRateCurrencyCode }}</span>
          </span>
          <button
            type="button"
            class="modal-close"
            :aria-label="$t('retailSale.paymentModal.close')"
            @click="cancel"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </header>

      <div
        v-if="loading || saving"
        class="payment-modal-loading-mask"
        role="status"
        aria-live="polite"
      >
        <div class="payment-modal-loading-box">
          <div class="payment-modal-loader" aria-hidden="true"></div>
          <div class="payment-modal-loading-copy">
            <strong>{{
              saving
                ? $t('retailSale.paymentModal.saving')
                : $t('retailSale.paymentModal.loading')
            }}</strong>
            <small>{{ $t('retailSale.loading.pleaseWait') }}</small>
          </div>
        </div>
      </div>

      <div class="payment-modal-body">
        <div class="reference-payment-columns">
          <main class="reference-payment-main">
          <section class="discount-panel payment-panel">
            <div class="panel-title-row">
              <h3>
                <i class="fas fa-tag"></i>
                {{ $t('retailSale.paymentModal.globalDiscount') }}
              </h3>
              <span class="applied-badge" v-if="globalDiscountAmount">
                {{ appliedDiscountLabel }}
              </span>
              <div class="discount-type-switch">
                <button
                  type="button"
                  :class="{ active: discountType === 'percent' }"
                  @click="discountType = 'percent'"
                >
                  % {{ $t('retailSale.paymentModal.percent') }}
                </button>
                <button
                  type="button"
                  :class="{ active: discountType === 'fixed' }"
                  @click="discountType = 'fixed'"
                >
                  $ {{ $t('retailSale.paymentModal.fixed') }}
                </button>
              </div>
            </div>
            <div class="discount-entry">
              <label class="discount-input">
                <span>{{
                  discountType === 'percent' ? '%' : currencySymbol
                }}</span>
                <input
                  v-model.number="discountValue"
                  type="number"
                  min="0"
                  :max="discountType === 'percent' ? 100 : grossAmount"
                  :aria-label="$t('retailSale.paymentModal.discountInput')"
                  @focus="focusDiscountInput"
                  @mouseup.prevent
                />
                <button
                  v-if="discountValue"
                  type="button"
                  class="clear-discount"
                  @click="discountValue = 0"
                >
                  <i class="fas fa-times"></i>
                </button>
              </label>
              <div class="quick-discounts">
                <button
                  v-for="value in quickDiscounts"
                  :key="value"
                  type="button"
                  :class="{ active: Number(discountValue) === value }"
                  @click="discountValue = value"
                >
                  {{
                    discountType === 'percent'
                      ? `${value}%`
                      : formatAmount(value)
                  }}
                </button>
              </div>
            </div>
          </section>

        <section class="breakdown-section">
          <div class="section-heading">
            <h3>
              <i class="fas fa-table"></i>
              {{ $t('retailSale.paymentModal.multiplePayment') }}
              <span>{{
                $t('retailSale.paymentModal.lines', {
                  count: paymentLines.length
                })
              }}</span>
            </h3>
          </div>
          <div v-if="paymentValidationMessages.length" class="payment-validation-summary">
            <div v-for="message in paymentValidationMessages" :key="message">
              {{ message }}
            </div>
          </div>
          <div class="payment-table">
            <div class="payment-table-head">
              <span>{{ $t('retailSale.paymentModal.paymentMethod') }}<b class="required-mark">*</b></span
              ><span>{{ $t('retailSale.paymentModal.bankName') }}<b v-if="bankFieldsRequired" class="required-mark">*</b></span
              ><span>{{ $t('retailSale.paymentModal.bankAccount') }}<b v-if="bankFieldsRequired" class="required-mark">*</b></span
              ><span>{{ $t('retailSale.paymentModal.currency') }}<b class="required-mark">*</b></span
              ><span>{{ $t('retailSale.paymentModal.amountTendered') }}<b class="required-mark">*</b></span
              ><span></span>
            </div>
            <div
              v-for="(line, index) in paymentLines"
              :key="line.key"
              class="payment-table-row"
            >
              <div class="payment-field">
                <select
                  v-model.number="line.payment_method_id"
                  :class="{ 'has-error': fieldError(index, 'payment_method_id') }"
                  @change="paymentMethodChanged(line)"
                >
                <option :value="null">{{
                  $t('retailSale.paymentModal.selectMethod')
                }}</option>
                <option
                  v-for="method in paymentMethods"
                  :key="method.payment_method_id"
                  :value="method.payment_method_id"
                  >{{ method.payment_method }}</option
                >
                </select>
              </div>
              <div v-if="requiresBank(line)" class="payment-field">
                <select
                  v-model.number="line.bank_id"
                  :class="{ 'has-error': fieldError(index, 'bank_id') }"
                  @change="bankChanged(line)"
                >
                <option :value="null">{{
                  $t('retailSale.paymentModal.selectBank')
                }}</option>
                <option
                  v-for="bank in banks"
                  :key="bank.bank_id"
                  :value="bank.bank_id"
                  >{{ bank.bank_name }}</option
                >
                </select>
              </div>
              <div v-else class="payment-field"><div class="payment-na">— N/A (Cash Drawer)</div></div>
              <div v-if="requiresBank(line)" class="payment-field">
                <select
                  v-model.number="line.bank_account_id"
                  :class="{ 'has-error': fieldError(index, 'bank_account_id') }"
                  :disabled="!line.bank_id"
                >
                <option :value="null">{{
                  $t('retailSale.paymentModal.selectAccount')
                }}</option>
                <option
                  v-for="account in accountsFor(line)"
                  :key="account.account_id"
                  :value="account.account_id"
                  >{{ account.account_number
                  }}{{
                    account.account_name ? ` (${account.account_name})` : ''
                  }}</option
                >
                </select>
              </div>
              <div v-else class="payment-field"><div class="payment-na">— N/A</div></div>
              <div class="payment-field">
                <select
                  v-model.number="line.input_currency_id"
                  class="currency-select"
                  :class="{ 'has-error': fieldError(index, 'input_currency_id') }"
                >
                <option
                  v-for="item in availableCurrencies"
                  :key="item.currency_id"
                  :value="item.currency_id"
                  >{{ item.currency_code }}</option
                >
                </select>
              </div>
              <div class="payment-field">
                <label class="amount-input" :class="{ 'has-error': fieldError(index, 'input_amount') }"
                  ><span>{{ currencySymbolFor(line.input_currency_id) }}</span
                  ><input
                    v-model.number="line.input_amount"
                    type="number"
                    min="0"
                    @focus="focusAmountInput($event, line)"
                    @mouseup.prevent
                /></label>
              </div>
              <button
                type="button"
                class="remove-line"
                :aria-label="$t('retailSale.paymentModal.removePayment')"
                @click="removeLine(index)"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
          <button type="button" class="add-payment" @click="addLine">
            <i class="fas fa-plus-circle"></i>
            {{ $t('retailSale.paymentModal.addPayment') }}
          </button>
        </section>

        <section class="change-panel change-currency-panel">
          <div class="change-content">
            <div class="change-header">
              <div class="change-title-block">
                <div class="change-title-row">
                  <span class="change-title-icon"><i class="fas fa-sync-alt"></i></span>
                  <h3>{{ $t('retailSale.paymentModal.changeReturnConfiguration') }}</h3>
                </div>
                <div class="change-due-line">
                  <span>{{ $t('retailSale.paymentModal.changeDue') }}:</span>
                  <strong>{{ currencySymbol }}{{ formatNumber(changeDue) }} / {{ formatConvertedNumber(changeDue * conversionRate) }} {{ convertedCurrencyCode }}</strong>
                  <span class="change-exact">{{ exactSettlement ? $t('retailSale.paymentModal.exactSettled') : $t('retailSale.paymentModal.reviewBalance') }}</span>
                </div>
              </div>
              <div class="change-currency-options" role="group" :aria-label="$t('retailSale.paymentModal.changeReturnCurrency')">
                <button type="button" :class="{ active: changeMode === 'KHR' }" :disabled="!khrCurrency" @click="changeMode = 'KHR'">៛ {{ $t('retailSale.paymentModal.khrOnly') }}</button>
                <button type="button" :class="{ active: changeMode === 'split' }" :disabled="!usdCurrency || !khrCurrency" @click="selectSplitChange">{{ $t('retailSale.paymentModal.splitChange') }}</button>
              </div>
            </div>
            <div class="change-summary-row">
              <div class="change-return-total">
                <span>{{ $t('retailSale.paymentModal.cashReturnUsd') }}</span>
                <strong>{{ formatUsd(returnedChange.usd) }}</strong>
              </div>
              <div class="change-return-total">
                <span>{{ $t('retailSale.paymentModal.cashReturnKhr') }}</span>
                <strong>៛ {{ formatCashAmount(returnedChange.riel, khrCurrency, 0) }}</strong>
              </div>
              <div class="change-summary-right">
                <div class="change-drawer-status">
                  <span>{{ $t('retailSale.paymentModal.drawerStatus') }}</span>
                  <strong>{{ formatUsd(returnedChange.usd) }} + {{ formatCashAmount(returnedChange.riel, khrCurrency, 0) }} KHR</strong>
                </div>
                <span class="change-state" :class="{ invalid: !changeMatches }"><i class="far fa-check-circle"></i> {{ changeDue ? (changeMatches ? $t('retailSale.paymentModal.changeReady') : $t('retailSale.paymentModal.reviewBalance')) : $t('retailSale.paymentModal.noChangePayout') }}</span>
              </div>
            </div>
            <p v-if="changeDue && !changeMatches" class="change-return-error">{{ $t('retailSale.paymentModal.changeReturnMismatch') }}</p>
          </div>
        </section>

          </main>

          <aside class="reference-payment-sidebar">
            <section class="due-panel payment-panel">
              <h3 class="sidebar-panel-title">
                {{ $t('retailSale.paymentModal.billSummary') }}
              </h3>
              <div class="due-meta">
                <span
                  >{{ $t('retailSale.paymentModal.gross') }}
                  <b>{{ formatAmount(grossAmount) }}</b></span
                >
                <span
                  >{{ $t('retailSale.paymentModal.discountShort') }}
                  <b class="negative"
                    >-{{ formatAmount(globalDiscountAmount) }}</b
                  ></span
                >
              </div>
              <div class="due-total">
                <span class="payment-modal-icon small"
                  ><i class="fas fa-money-bill-wave"></i
                ></span>
                <div>
                  <small>{{ $t('retailSale.paymentModal.targetNetDue') }}</small
                  ><div class="due-amount">
                    <span>{{ currencySymbol }}</span
                    ><strong>{{ formatNumber(targetNetDue) }}</strong>
                  </div>
                </div>
                <div class="converted-due">
                  <b>{{ convertedTargetDue }}</b
                  ><small>{{
                    $t('retailSale.paymentModal.exactSettlement')
                  }}</small>
                </div>
              </div>
            </section>

            <section class="settlement-panel payment-panel">
              <h3>
                <i class="fas fa-receipt"></i>
                {{ $t('retailSale.paymentModal.tenderSummary') }}
              </h3>
              <div>
                <span>{{ $t('retailSale.paymentModal.targetNetDue') }}:</span
                ><b
                  >{{ formatAmount(targetNetDue) }} {{ currencyCode }}
                  <small>({{ convertedTargetDue }})</small></b
                >
              </div>
              <div>
                <span>{{ $t('retailSale.paymentModal.totalTender') }}:</span
                ><b>{{ formatAmount(totalTender) }} {{ currencyCode }}</b>
              </div>
              <div>
                <span>{{ $t('retailSale.paymentModal.outstanding') }}:</span
                ><b
                  >{{ formatAmount(outstandingBalance) }} {{ currencyCode }}
                  <small>{{ convertedOutstanding }}</small>
                  <small v-if="!outstandingBalance"
                    >({{ $t('retailSale.paymentModal.fullySettled') }})</small
                  ></b
                >
              </div>
            </section>

            <section class="numpad-panel payment-panel">
            <div class="numpad-title">
              <h3>
                <i class="fas fa-th"></i>
                {{ $t('retailSale.paymentModal.touchNumpad') }}
              </h3>
              <small>{{ $t('retailSale.paymentModal.quickAmount') }}</small>
            </div>
            <div class="numpad-grid">
              <button
                v-for="key in numpadKeys"
                :key="key"
                type="button"
                  :class="{ muted: key === 'C' || key === '.' }"
                @mousedown.prevent
                @click="pressNumpad(key)"
              >
                {{ key }}
              </button>
            </div>
            </section>
          </aside>
        </div>
      </div>

      <footer class="payment-modal-footer">
        <label class="city-ledger"
          ><input v-model="cityLedger" type="checkbox" />
          {{ $t('retailSale.paymentModal.cityLedger') }} <kbd>L</kbd></label
        >
        <div class="footer-actions">
          <button
            v-if="isDevelopment"
            type="button"
            class="preview-button"
            :disabled="loading || saving"
            @click="$emit('preview-receipt', paymentPayload())"
          >
            <i class="fas fa-eye"></i>
            {{ $t('retailSale.receipt.preview') }}
          </button>
          <button type="button" class="cancel-button" @click="cancel">
            <i class="fas fa-arrow-left"></i>
            {{ $t('retailSale.paymentModal.cancel') }} <kbd>ESC</kbd></button
          ><button
            type="button"
            class="finish-button"
            :disabled="loading || saving || outstandingBalance > settlementTolerance || !changeMatches"
            @click="finish"
          >
            <i class="fas fa-print"></i>
            {{ $t('retailSale.paymentModal.finish') }} <kbd>F12</kbd>
          </button>
        </div>
      </footer>
    </section>
  </div>
</template>

<script>
import { formatCurrency, formatExchangeRate } from '../../utils/currency'
import { calculateCashChange, calculateOtherChange } from '../../utils/change'

export default {
  name: 'CompletePaymentModal',
  props: {
    visible: { type: Boolean, default: false },
    order: { type: Object, required: true },
    customerName: { type: String, default: '' },
    currency: { type: Object, default: () => ({}) },
    currencies: { type: Array, default: () => [] },
    exchangeRates: { type: Array, default: () => [] },
    paymentResources: {
      type: Object,
      default: () => ({ payment_methods: [], banks: [] })
    },
    validationErrors: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false }
  },
  data() {
    return {
      discountType: 'percent',
      discountValue: 0,
      initialDiscountType: 'percent',
      initialDiscountValue: 0,
      initialDiscountAmount: 0,
      paymentLines: [],
      cityLedger: false,
      changeMode: 'split',
      splitChangeUsd: 0,
      splitChangeRiel: 0,
      nextLineKey: 1,
      numpadValue: '',
      numpadTarget: null
    }
  },
  computed: {
    isDevelopment() {
     return false;
    },
    paymentMethods() {
      return this.paymentResources.payment_methods || []
    },
    banks() {
      return this.paymentResources.banks || []
    },
    availableCurrencies() {
      return this.currencies.filter(item => {
        if (this.sameId(item.currency_id, this.currency.currency_id)) return true

        return this.exchangeRates.some(
          rate =>
            this.sameId(rate.currency_id_to, item.currency_id) &&
            Number(rate.fx_rate_ratio) > 0
        )
      })
    },
    grossAmount() {
      return Number(this.order.grand_total || 0)
    },
    globalDiscountAmount() {
      const value = Math.max(Number(this.discountValue) || 0, 0)
      return Math.min(
        this.discountType === 'percent'
          ? (this.grossAmount * Math.min(value, 100)) / 100
          : value,
        this.grossAmount
      )
    },
    targetNetDue() {
      return Math.max(this.grossAmount - this.globalDiscountAmount, 0)
    },
    totalTender() {
      return this.paymentLines.reduce(
        (sum, line) =>
          sum +
          (Number(line.input_amount) || 0) *
            this.rateFor(line.input_currency_id),
        0
      )
    },
    outstandingBalance() {
      const difference = this.targetNetDue - this.totalTender
      return difference > this.settlementTolerance ? difference : 0
    },
    changeDue() {
      const difference = this.totalTender - this.targetNetDue
      return difference > this.settlementTolerance ? difference : 0
    },
    usdCurrency() {
      return this.availableCurrencies.find(item => item.currency_code === 'USD')
    },
    khrCurrency() {
      return this.availableCurrencies.find(item => item.currency_code === 'KHR')
    },
    returnedChange() {
      if (this.changeMode === 'split') {
        return { usd: Number(this.splitChangeUsd) || 0, riel: Number(this.splitChangeRiel) || 0 }
      }
      const allocation = calculateCashChange({
        amount: this.changeDue,
        preferredCode: 'KHR',
        usdCurrency: null,
        khrCurrency: this.khrCurrency,
        usdRate: 0,
        khrRate: this.khrCurrency ? this.rateFor(this.khrCurrency.currency_id) : 0,
        tolerance: this.khrChangeTolerance
      })
      return { usd: 0, riel: allocation.riel }
    },
    changeMatches() {
      const usd = Number(this.returnedChange.usd)
      const riel = Number(this.returnedChange.riel)
      if (!Number.isFinite(usd) || !Number.isFinite(riel) || usd < 0 || riel < 0) return false
      if (Math.abs(usd * 100 - Math.round(usd * 100)) > 0.000001 || Math.abs(riel - Math.round(riel)) > 0.000001) return false
      if (usd && !this.usdCurrency || riel && !this.khrCurrency) return false
      const usdUnit = this.cashUnit(this.usdCurrency, 2)
      const rielUnit = this.cashUnit(this.khrCurrency, 0)
      if (usd && (!usdUnit || Math.abs(usd / usdUnit - Math.round(usd / usdUnit)) > 0.000001)) return false
      if (riel && (!rielUnit || Math.abs(riel / rielUnit - Math.round(riel / rielUnit)) > 0.000001)) return false
      const returned = usd * (this.usdCurrency ? this.rateFor(this.usdCurrency.currency_id) : 0) +
        riel * (this.khrCurrency ? this.rateFor(this.khrCurrency.currency_id) : 0)
      const tolerance = riel > 0 ? this.khrChangeTolerance : this.settlementTolerance
      return Math.abs(returned - this.changeDue) <= tolerance + 0.000000001
    },
    khrChangeTolerance() {
      const khrRate = this.khrCurrency ? this.rateFor(this.khrCurrency.currency_id) : 0
      return Math.max(this.settlementTolerance, this.cashUnit(this.khrCurrency, 0) * khrRate / 2)
    },
    exactSettlement() {
      return (
        Math.abs(this.totalTender - this.targetNetDue) <=
        this.settlementTolerance
      )
    },
    settlementTolerance() {
      const configuredRounding = this.currency.currency_rounding
      const rounding = Math.max(
        Number(
          configuredRounding === null || configuredRounding === undefined
            ? 2
            : configuredRounding
        ),
        0
      )

      return 0.5 * Math.pow(10, -rounding)
    },
    currencyCode() {
      return this.currency.currency_code || ''
    },
    currencySymbol() {
      return this.currency.currency_code === 'KHR' ||
        this.currency.currency_symbol === 'R'
        ? '៛'
        : this.currency.currency_symbol || '$'
    },
    convertedCurrencyCode() {
      const rate = this.displayExchangeRate
      return rate ? rate.to_currency_code || '' : ''
    },
    conversionRate() {
      const rate = this.displayExchangeRate
      return Number((rate && rate.fx_rate) || 1)
    },
    convertedTargetDue() {
      return `${new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0
      }).format(this.targetNetDue * this.conversionRate)} ${
        this.convertedCurrencyCode
      }`
    },
    convertedOutstanding() {
      return `${new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0
      }).format(this.outstandingBalance * this.conversionRate)} ${
        this.convertedCurrencyCode
      }`
    },
    exchangeRateLabel() {
      const rate = this.displayExchangeRate
      return rate
        ? `1 ${this.currencyCode} = ${formatExchangeRate(rate.fx_rate)} ${
            rate.to_currency_code || ''
          }`
        : ''
    },
    exchangeRateAmount() {
      const rate = this.displayExchangeRate
      return rate
        ? `${rate.to_currency_symbol || rate.to_currency_code || ''}${formatExchangeRate(
            rate.fx_rate
          )}`
        : ''
    },
    exchangeRateCurrencyCode() {
      const rate = this.displayExchangeRate
      return rate ? rate.to_currency_code || '' : ''
    },
    displayExchangeRate() {
      return this.exchangeRates.find(
        rate =>
          Number(rate.fx_rate) > 0 && Number(rate.fx_rate_ratio) > 0
      )
    },
    appliedDiscountLabel() {
      return this.discountType === 'percent'
        ? `${Number(this.discountValue) || 0}% Applied (-${this.formatAmount(
            this.globalDiscountAmount
          )})`
        : `-${this.formatAmount(this.globalDiscountAmount)}`
    },
    paymentValidationMessages() {
      return Object.keys(this.validationErrors || {}).reduce((messages, key) => {
        const errors = this.validationErrors[key]
        return messages.concat(
          Array.isArray(errors) ? errors : [String(errors)]
        )
      }, [])
    },
    bankFieldsRequired() {
      return this.paymentLines.some(line => this.requiresBank(line))
    },
    quickDiscounts() {
      return this.discountType === 'percent'
        ? [0, 5, 10, 15, 20]
        : [1, 2, 5, 10, 20]
    },
    numpadKeys() {
      return ['7', '8', '9', '4', '5', '6', '1', '2', '3', 'C', '0', '.']
    }
  },
  watch: {
    discountType() {
      this.emitDiscountChange()
    },
    discountValue() {
      this.emitDiscountChange()
    },
    changeDue() {
      if (this.changeMode === 'split') this.suggestSplitChange()
    },
    visible(value) {
      if (value) {
        this.initialize()
        this.$nextTick(
          () =>
            this.$el.querySelector('button, input, select') &&
            this.$el.querySelector('button, input, select').focus()
        )
        return
      }

      this.resetPaymentForm()
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    cashUnit(currency, fallbackRounding) {
      if (!currency) return 0
      const configured = Number(currency.minimum_cash_denomination)
      const minimum = currency.currency_code === 'KHR' ? 1 : currency.currency_code === 'USD' ? 0.01 : 0
      if (configured > 0) return Math.max(configured, minimum)
      if (minimum) return minimum
      const rounding = currency.currency_rounding == null ? fallbackRounding : Number(currency.currency_rounding)
      return Math.pow(10, -Math.max(Number.isFinite(rounding) ? rounding : fallbackRounding, 0))
    },
    selectSplitChange() {
      this.changeMode = 'split'
      this.suggestSplitChange()
    },
    suggestSplitChange() {
      const usdRate = this.usdCurrency ? this.rateFor(this.usdCurrency.currency_id) : 0
      const khrRate = this.khrCurrency ? this.rateFor(this.khrCurrency.currency_id) : 0
      if (usdRate <= 0 || khrRate <= 0) {
        this.splitChangeUsd = 0
        this.splitChangeRiel = 0
        return
      }

      // Prefer whole dollars, then add the smallest USD cents needed to
      // balance the KHR cash denomination and exchange rate.
      const wholeUsd = Math.floor(this.changeDue / usdRate + 0.000000001)
      const usdUnit = this.cashUnit(this.usdCurrency, 2)
      const khrUnit = this.cashUnit(this.khrCurrency, 0)
      const firstUsdUnits = Math.ceil((wholeUsd - 0.000000001) / usdUnit)
      const lastUsdUnits = Math.floor((this.changeDue / usdRate + 0.000000001) / usdUnit)

      for (let units = firstUsdUnits; units <= lastUsdUnits; units++) {
        const usd = Number((units * usdUnit).toFixed(4))
        if (Math.abs(usd * 100 - Math.round(usd * 100)) > 0.000001) continue
        const riel = calculateOtherChange({
          amount: this.changeDue,
          entered: usd,
          enteredRate: usdRate,
          otherRate: khrRate,
          otherUnit: khrUnit,
          precision: 2
        })
        if (Math.abs(usd * usdRate + riel * khrRate - this.changeDue) <= this.khrChangeTolerance + 0.000000001) {
          this.splitChangeUsd = usd
          this.splitChangeRiel = riel
          return
        }
      }

      const fallback = calculateCashChange({
        amount: this.changeDue,
        preferredCode: 'USD',
        usdCurrency: this.usdCurrency,
        khrCurrency: this.khrCurrency,
        usdRate,
        khrRate,
        tolerance: this.khrChangeTolerance
      })
      this.splitChangeUsd = fallback.usd
      this.splitChangeRiel = fallback.riel
    },
    updateSplitChange(editedCurrency) {
      const usdRate = this.usdCurrency ? this.rateFor(this.usdCurrency.currency_id) : 0
      const khrRate = this.khrCurrency ? this.rateFor(this.khrCurrency.currency_id) : 0
      if (editedCurrency === 'USD') {
        this.splitChangeRiel = calculateOtherChange({
          amount: this.changeDue,
          entered: this.splitChangeUsd,
          enteredRate: usdRate,
          otherRate: khrRate,
          otherUnit: this.cashUnit(this.khrCurrency, 0),
          precision: 2
        })
      } else {
        this.splitChangeUsd = calculateOtherChange({
          amount: this.changeDue,
          entered: this.splitChangeRiel,
          enteredRate: khrRate,
          otherRate: usdRate,
          otherUnit: this.cashUnit(this.usdCurrency, 2),
          precision: 2
        })
      }
    },
    resetPaymentForm() {
      this.paymentLines = []
      this.cityLedger = false
      this.changeMode = 'split'
      this.splitChangeUsd = 0
      this.splitChangeRiel = 0
      this.nextLineKey = 1
      this.numpadValue = ''
      this.numpadTarget = null
    },
    initialize() {
      this.resetPaymentForm()
      this.initialDiscountType = this.order.invoice_discount_type || 'percent'
      this.initialDiscountValue = Number(this.order.invoice_discount_rate || 0)
      this.initialDiscountAmount = Number(this.order.invoice_discount_amount || 0)
      this.discountType = this.initialDiscountType
      this.discountValue = this.initialDiscountValue
      this.addLine()
    },
    emitDiscountChange() {
      this.$emit('discount-change', {
        discount_type: this.discountType,
        discount_rate: this.discountType === 'percent'
          ? Number(this.discountValue) || 0
          : 0,
        discount: this.globalDiscountAmount
      })
    },
    addLine() {
      this.paymentLines.push({
        key: this.nextLineKey++,
        payment_method_id: null,
        bank_id: null,
        bank_account_id: null,
        input_currency_id:
          this.currency.currency_id ||
          (this.availableCurrencies[0] &&
            this.availableCurrencies[0].currency_id) ||
          null,
        input_amount: 0
      })
    },
    fieldError(index, field) {
      const errors = this.validationErrors || {}
      const specific = errors[`payment.payment_lines.${index}.${field}`]
      if (specific) return this.formatValidationError(specific)

      return ''
    },
    formatValidationError(error) {
      return Array.isArray(error) ? error.join(', ') : String(error)
    },
    removeLine(index) {
      if (this.paymentLines.length > 1) this.paymentLines.splice(index, 1)
    },
    requiresBank(line) {
      const method = this.paymentMethods.find(
        item => this.sameId(item.payment_method_id, line.payment_method_id)
      )
      return Boolean(method && method.requires_bank_account)
    },
    paymentMethodChanged(line) {
      if (!this.requiresBank(line)) {
        line.bank_id = null
        line.bank_account_id = null
        return
      }

      const accounts = this.banks.reduce((options, bank) => {
        return options.concat((bank.accounts || []).map(account => ({
          bankId: bank.bank_id,
          accountId: account.account_id
        })))
      }, [])
      if (accounts.length === 1) {
        line.bank_id = accounts[0].bankId
        line.bank_account_id = accounts[0].accountId
        return
      }

      this.bankChanged(line)
    },
    bankChanged(line) {
      const accounts = this.accountsFor(line)
      if (accounts.length === 1) {
        line.bank_account_id = accounts[0].account_id
        return
      }

      if (
        !accounts.some(account =>
          this.sameId(account.account_id, line.bank_account_id)
        )
      ) {
        line.bank_account_id = null
      }
    },
    accountsFor(line) {
      const bank = this.banks.find(item => this.sameId(item.bank_id, line.bank_id))
      return bank ? bank.accounts || [] : []
    },
    sameId(left, right) {
      return left != null && right != null && Number(left) === Number(right)
    },
    rateFor(currencyId) {
      if (!currencyId || this.sameId(currencyId, this.currency.currency_id)) {
        return 1
      }
      const rate = this.exchangeRates.find(
        item => this.sameId(item.currency_id_to, currencyId)
      )
      return rate && Number(rate.fx_rate_ratio) > 0
        ? Number(rate.fx_rate_ratio)
        : 0
    },
    currencySymbolFor(currencyId) {
      const item =
        this.currencies.find(currency =>
          this.sameId(currency.currency_id, currencyId)
        ) ||
        this.currency
      return item.currency_code === 'KHR' || item.currency_symbol === 'R'
        ? '៛'
        : item.currency_symbol || item.currency_code || '$'
    },
    formatAmount(value) {
      return formatCurrency(value, this.currency)
    },
    formatNumber(value) {
      const configuredRounding = this.currency.currency_rounding
      const rounding = Math.max(
        Number(
          configuredRounding === null || configuredRounding === undefined
            ? 2
            : configuredRounding
        ),
        0
      )

      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: rounding,
        maximumFractionDigits: rounding
      }).format(Number(value) || 0)
    },
    formatConvertedNumber(value) {
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0
      }).format(Number(value) || 0)
    },
    formatUsd(value) {
      return `$${this.formatCashAmount(value, this.usdCurrency, 2)}`
    },
    formatCashAmount(value, currency, fallbackRounding) {
      const rounding = currency && currency.currency_rounding != null
        ? Math.max(Number(currency.currency_rounding), 0)
        : fallbackRounding
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: rounding,
        maximumFractionDigits: rounding
      }).format(Number(value) || 0)
    },
    pressNumpad(key) {
      if (!this.numpadTarget) {
        const line = this.paymentLines[0]
        if (!line) return
        this.setNumpadTarget({ type: 'amount', lineKey: line.key })
      }

      if (key === 'C') {
        this.numpadValue = ''
        this.setNumpadTargetValue(0)
        return
      }
      if (key === '.' && this.numpadValue.includes('.')) return
      this.numpadValue += key
      this.setNumpadTargetValue(Number(this.numpadValue) || 0)
    },
    setNumpadTarget(target) {
      this.numpadTarget = target
      this.numpadValue = ''
    },
    focusDiscountInput(event) {
      this.setNumpadTarget({ type: 'discount' })
      event.target.select()
    },
    focusAmountInput(event, line) {
      this.setNumpadTarget({ type: 'amount', lineKey: line.key })
      event.target.select()
    },
    setNumpadTargetValue(value) {
      if (!this.numpadTarget) return

      if (this.numpadTarget.type === 'discount') {
        this.discountValue = value
        return
      }

      const line = this.paymentLines.find(
        item => item.key === this.numpadTarget.lineKey
      )
      if (line) line.input_amount = value
    },
    handleKeydown(event) {
      if (!this.visible) return
      if (event.key === 'Escape') {
        event.preventDefault()
        this.cancel()
      }
      if (event.key === 'F12') {
        event.preventDefault()
        this.finish()
      }
      if (
        event.key.toLowerCase() === 'l' &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault()
        this.cityLedger = !this.cityLedger
      }
    },
    cancel() {
      if (this.loading || this.saving) return

      this.$emit('discount-change', {
        discount_type: this.initialDiscountType,
        discount_rate: this.initialDiscountType === 'percent'
          ? this.initialDiscountValue
          : 0,
        discount: this.initialDiscountAmount
      })
      this.$emit('cancel')
    },
    finish() {
      if (this.loading || this.saving) return
      if (this.outstandingBalance > this.settlementTolerance) return
      if (!this.changeMatches) return

      this.$emit('finish', this.paymentPayload())
    },
    paymentPayload() {
      return {
        discount_type: this.discountType,
        discount_rate:
          this.discountType === 'percent' ? Number(this.discountValue) || 0 : 0,
        discount: this.globalDiscountAmount,
        change_in_usd: this.returnedChange.usd,
        change_in_riel: this.returnedChange.riel,
        payment_lines: this.paymentLines.map(line => ({
          payment_method_id: line.payment_method_id,
          bank_id: line.bank_id,
          bank_account_id: line.bank_account_id,
          input_currency_id: line.input_currency_id,
          input_amount: Number(line.input_amount) || 0,
          base_currency_id: this.currency.currency_id || null,
          ratio_rate_to_base: this.rateFor(line.input_currency_id),
          to_other_currency:
            (Number(line.input_amount) || 0) *
            this.rateFor(line.input_currency_id)
        })),
        is_city_ledger: this.cityLedger
      }
    }
  }
}
</script>

<style scoped src="../../styles/complete-payment-modal.css"></style>
